import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User, Package, LogOut, Clock, CheckCircle, XCircle, Truck, Phone, MapPin, Building, Edit, Save } from "lucide-react";

export default function MiCuenta() {
  const { user, logout, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    rut: ""
  });

  useEffect(() => {
    if (user) {
      fetchOrders();
      setProfileData({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        rut: user.rut || ""
      });
    }
  }, [user]);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await api.get("/orders/my-orders");
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", profileData);
      toast.success("Perfil actualizado");
      setEditing(false);
      checkAuth(); // Refresh user data
    } catch (error) {
      toast.error("Error al actualizar perfil");
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-UY", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-UY", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Pendiente", icon: Clock, variant: "secondary" },
      processing: { label: "Procesando", icon: Package, variant: "default" },
      shipped: { label: "Enviado", icon: Truck, variant: "default" },
      completed: { label: "Completado", icon: CheckCircle, variant: "success" },
      cancelled: { label: "Cancelado", icon: XCircle, variant: "destructive" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getAccountStatusBadge = () => {
    if (user?.status === "pending") {
      return (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-lg mt-4">
          <p className="font-medium">Tu cuenta está pendiente de aprobación</p>
          <p className="text-sm mt-1">Te notificaremos cuando sea verificada para que puedas realizar pedidos.</p>
        </div>
      );
    }
    if (user?.status === "rejected") {
      return (
        <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-lg mt-4">
          <p className="font-medium">Tu cuenta ha sido rechazada</p>
          <p className="text-sm mt-1">Contactanos por WhatsApp para más información.</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background" data-testid="mi-cuenta-login">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <User className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="font-heading text-2xl md:text-3xl mb-2">MI CUENTA</h1>
          <p className="text-muted-foreground mb-6">
            Ingresá a tu cuenta para ver tus pedidos y gestionar tu perfil
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login">
              <Button className="bg-primary hover:bg-primary/90">
                Iniciar Sesión
              </Button>
            </Link>
            <Link to="/registro">
              <Button variant="outline">
                Crear Cuenta
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="mi-cuenta-page">
      {/* Header */}
      <div className="bg-primary text-white py-8 relative">
        <div className="relative industrial-texture"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl">{user.name}</h1>
              <p className="text-white/80">{user.email}</p>
              {user.status === "approved" && (
                <span className="inline-flex items-center mt-1 text-xs bg-green-500/20 text-green-100 px-2 py-0.5 rounded">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Cuenta verificada
                </span>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={logout}
            className="text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>
      </div>

      {getAccountStatusBadge() && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {getAccountStatusBadge()}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="orders">
          <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0 mb-8">
            <TabsTrigger
              value="orders"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent font-heading uppercase tracking-wide px-6"
            >
              <Package className="w-4 h-4 mr-2" />
              Mis Pedidos
            </TabsTrigger>
            <TabsTrigger
              value="profile"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent font-heading uppercase tracking-wide px-6"
            >
              <User className="w-4 h-4 mr-2" />
              Mi Perfil
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            {user.status !== "approved" ? (
              <div className="text-center py-12 bg-white rounded-sm border">
                <Clock className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                <p className="font-medium text-lg mb-2">Cuenta pendiente de verificación</p>
                <p className="text-muted-foreground">
                  Una vez que tu cuenta sea aprobada, podrás realizar pedidos.
                </p>
              </div>
            ) : loadingOrders ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-sm border">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No tenés pedidos todavía</p>
                <Link to="/tienda">
                  <Button>Ver Productos</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.order_id}
                    className="bg-white rounded-sm border p-4 md:p-6"
                    data-testid={`order-${order.order_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <p className="font-heading text-lg">Pedido #{order.order_id.slice(-8)}</p>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total</p>
                        <p className="font-heading text-xl text-accent">
                          {formatPrice(order.total)}
                        </p>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm font-medium mb-2">Productos:</p>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {item.name} x{item.quantity}
                            </span>
                            <span>{formatPrice(item.price * item.quantity)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {order.notes && (
                      <div className="border-t mt-4 pt-4">
                        <p className="text-sm">
                          <span className="font-medium">Notas:</span> {order.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile">
            <div className="bg-white rounded-sm border p-6 max-w-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl">Mi Información</h2>
                {!editing ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                      Cancelar
                    </Button>
                    <Button size="sm" onClick={handleSaveProfile} disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? "Guardando..." : "Guardar"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <User className="w-4 h-4" />
                    Nombre
                  </Label>
                  {editing ? (
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="font-medium mt-1">{user.name}</p>
                  )}
                </div>

                <div>
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    Email (no editable)
                  </Label>
                  <p className="font-medium mt-1">{user.email}</p>
                </div>

                <div>
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    Teléfono / WhatsApp
                  </Label>
                  {editing ? (
                    <Input
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="font-medium mt-1">{user.phone || "-"}</p>
                  )}
                </div>

                <div>
                  <Label className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    Dirección
                  </Label>
                  {editing ? (
                    <Input
                      value={profileData.address}
                      onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="font-medium mt-1">{user.address || "-"}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Ciudad</Label>
                    {editing ? (
                      <Input
                        value={profileData.city}
                        onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium mt-1">{user.city || "-"}</p>
                    )}
                  </div>
                  <div>
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <Building className="w-4 h-4" />
                      Cédula / RUT
                    </Label>
                    {editing ? (
                      <Input
                        value={profileData.rut}
                        onChange={(e) => setProfileData({ ...profileData, rut: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <p className="font-medium mt-1">{user.rut || "-"}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
